// Small building blocks shared by the admin pages: toasts, confirm modals, status pills,
// sparklines, date and money formatting, and a data loader.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'

import { errorText } from '../../api/adminV2'

/* ---------- formatting ---------- */

export const formatDate = (value, empty = 'n/a') => {
  if (!value) return empty
  const m = moment(value)
  return m.isValid() ? m.format('MMM D, YYYY') : empty
}

export const formatNumber = (n) =>
  n === null || n === undefined ? '0' : Number(n).toLocaleString()

export const money = (dollars) =>
  dollars === null || dollars === undefined || dollars === ''
    ? 'n/a'
    : `$${Number(dollars).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`

export const moneyFromCents = (cents) =>
  cents === null || cents === undefined ? 'n/a' : money(Number(cents) / 100)

export const initials = (name = '') =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

export const firstName = (name = '') => (name || '').trim().split(/\s+/)[0] || 'this person'

/* ---------- status pills ---------- */

const STATUS = {
  paying: ['good', 'Paying'],
  free_pro: ['good', 'Free Pro'],
  lapsed: ['bad', 'Lapsed'],
  free: ['plain', 'Free'],
  ending: ['warn', 'Ending soon'],
  paused: ['warn', 'Paused'],
  expired: ['bad', 'Expired'],
  closed: ['plain', 'Closed'],
  unconfirmed: ['warn', 'Email not confirmed'],
  published: ['good', 'Published'],
  draft: ['plain', 'Draft'],
  flagged: ['bad', 'Flagged'],
  accepted: ['good', 'Accepted'],
  pending: ['warn', 'Invite not accepted'],
  strong: ['bad', 'Strong match'],
  possible: ['warn', 'Possible match'],
  same: ['bad', 'Same person'],
  different: ['plain', 'Not the same person'],
}

export const Pill = ({ status, kind, children }) => {
  const [tone, label] = STATUS[status] || [kind || 'plain', status || 'Unknown']
  return <span className={`a-pill a-${kind || tone}`}>{children || label}</span>
}

export const RECIPE_SOURCE = { scan: 'Scanned card', link: 'From a link', typed: 'Typed in' }

export const recipeStatusKey = (status) =>
  ({ 1: 'published', 5: 'draft', 8: 'flagged' }[Number(status)] || 'draft')

/* ---------- sparkline ---------- */

export const Spark = ({ points, label }) => {
  const values = (points || []).map((p) => Number(p) || 0)
  if (values.length < 2) return null
  const max = Math.max(...values, 1)
  const w = 120
  const h = 26
  const xy = values.map((p, i) => [(i / (values.length - 1)) * w, h - 2 - (p / max) * (h - 6)])
  const line = xy.map(([x, y], i) => (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)).join(' ')
  const [lx, ly] = xy[xy.length - 1]
  return (
    <svg
      className="a-spark"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {label ? <title>{label}</title> : null}
      <path d={`${line} L${w} ${h} L0 ${h} Z`} fill="var(--pale)" opacity="0.7" />
      <path
        d={line}
        fill="none"
        stroke="var(--ink)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={lx} cy={ly} r="2.5" fill="var(--gold)" stroke="var(--ink)" strokeWidth="1" />
    </svg>
  )
}

/* ---------- toasts and confirm modals ---------- */

const FeedbackContext = createContext({
  toast: () => {},
  toastError: () => {},
  confirm: () => Promise.resolve(false),
})

export const useFeedback = () => useContext(FeedbackContext)

// A modal that asks before a risky action. With `typed`, the button only works once the
// admin types that exact text. With `render(value, setValue)`, extra inputs are shown; their
// value starts as `initial`, must pass `valid(value)`, and is handed to `run(value)`.
const ConfirmModal = ({ options, onClose }) => {
  const {
    title,
    text,
    action = 'OK',
    danger = false,
    typed = null,
    typedLabel,
    render = null,
    initial = null,
    valid = null,
    run = null,
  } = options
  const [value, setValue] = useState('')
  const [extra, setExtra] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef(null)
  const okRef = useRef(null)
  const ready = (!typed || value.trim() === typed) && (!valid || valid(extra))

  useEffect(() => {
    if (typed && inputRef.current) inputRef.current.focus()
    else if (!render && okRef.current) okRef.current.focus()
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const ok = () => {
    if (!ready || busy) return
    if (!run) return onClose(true)
    setBusy(true)
    setError('')
    Promise.resolve()
      .then(() => run(extra))
      .then((result) => onClose(result === undefined ? true : result))
      .catch((e) => {
        setBusy(false)
        setError(typeof e === 'string' ? e : errorText(e))
      })
  }

  return (
    <div
      className="a-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose(false)
      }}
    >
      <div className="a-modal" role="dialog" aria-modal="true" aria-labelledby="a-modal-title">
        <h2 id="a-modal-title">{title}</h2>
        {text ? <p>{text}</p> : null}
        {render ? render(extra, setExtra, ok) : null}
        {typed ? (
          <input
            ref={inputRef}
            type="text"
            value={value}
            placeholder={typed}
            aria-label={typedLabel || `Type ${typed} to confirm`}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ok()}
          />
        ) : null}
        {error ? (
          <p className="a-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="a-btn-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="a-btn" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </button>
          <button
            ref={okRef}
            type="button"
            className={`a-btn ${danger ? 'a-danger' : 'a-primary'}`}
            aria-disabled={!ready || busy}
            onClick={ok}
          >
            {busy ? 'Working…' : action}
          </button>
        </div>
      </div>
    </div>
  )
}

export const FeedbackProvider = ({ children }) => {
  const [toastState, setToastState] = useState(null)
  const [modal, setModal] = useState(null)
  const timer = useRef(null)

  const show = useCallback((message, error) => {
    clearTimeout(timer.current)
    setToastState({ message, error })
    timer.current = setTimeout(() => setToastState(null), error ? 6000 : 2800)
  }, [])

  const toast = useCallback((message) => show(message, false), [show])
  const toastError = useCallback(
    (e, fallback) => show(typeof e === 'string' ? e : errorText(e, fallback), true),
    [show]
  )

  // Resolves true when the admin confirms (and `run`, if given, succeeded), false otherwise
  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setModal({ options, resolve })
      }),
    []
  )

  useEffect(() => () => clearTimeout(timer.current), [])

  const close = (result) => {
    if (modal) modal.resolve(result)
    setModal(null)
  }

  return (
    <FeedbackContext.Provider value={{ toast, toastError, confirm }}>
      {children}
      {modal ? <ConfirmModal options={modal.options} onClose={close} /> : null}
      {toastState ? (
        <div
          className={`a-toast ${toastState.error ? 'a-toast-error' : ''}`}
          role={toastState.error ? 'alert' : 'status'}
          onClick={() => setToastState(null)}
        >
          {toastState.message}
        </div>
      ) : null}
    </FeedbackContext.Provider>
  )
}

/* ---------- data loading ---------- */

// Loads data with `fn` whenever `deps` change; keeps the last result while reloading
export const useLoad = (fn, deps) => {
  const [state, setState] = useState({ data: null, error: null, loading: true })
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    setState((s) => ({ ...s, loading: true, error: null }))
    Promise.resolve()
      .then(fn)
      .then((data) => alive && setState({ data, error: null, loading: false }))
      .catch((error) => alive && setState((s) => ({ ...s, error, loading: false })))
    return () => {
      alive = false
    }
  }, [...deps, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { ...state, reload, setData: (data) => setState((s) => ({ ...s, data })) }
}

export const LoadError = ({ error, onRetry }) => (
  <section className="a-panel">
    <p className="a-error" role="alert">
      {errorText(error, 'This page could not be loaded.')}
    </p>
    {onRetry ? (
      <div>
        <button type="button" className="a-btn a-small" onClick={onRetry}>
          Try again
        </button>
      </div>
    ) : null}
  </section>
)

export const Loading = ({ text = 'Loading…' }) => <p className="a-loading">{text}</p>

// Downloads a spreadsheet that the API returned as raw bytes
export const saveFile = (bytes, name) => {
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'recipes.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const copyText = (text) => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text)
  return new Promise((resolve, reject) => {
    const el = document.createElement('textarea')
    el.value = text
    el.setAttribute('readonly', '')
    el.style.position = 'fixed'
    el.style.opacity = '0'
    document.body.appendChild(el)
    el.select()
    const ok = document.execCommand('copy')
    el.remove()
    if (ok) resolve()
    else reject(new Error('Copy failed'))
  })
}
